import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import DoctorSettings from '../components/doctorTabs/DoctorSettings';
import PrescriptionFieldsTab from '../components/doctorTabs/PrescriptionFieldsTab';
import { getDoctorById, getDoctorSettings } from '../redux/doctorSlice';
import { tryingnavigate, setPendingNavigation, clearPendingNavigation } from '../redux/blockingSlice';

function DoctorSettingsPage() {
    const { t } = useTranslation(['doctorprofile']);
    const dispatch = useDispatch();
    const { selectedDoctor } = useSelector((state) => state.doctor);
    const { resnavigate, pendingNavigation } = useSelector((state) => state.blocking);
    const bootstrapped = useRef(false);
    const doctorId = localStorage.getItem('ID');

    const [activeTab, setActiveTab] = useState('calendar');

    // Tab switches inside Settings get the same unsaved-changes treatment as
    // sidebar navigation: if the active tab marked itself dirty (`resnavigate`),
    // capture the requested tab as pending and let the dirty tab's discard
    // handler resolve it. We then auto-apply the tab change here.
    const handleTabChange = (key) => {
        if (key === activeTab) return;
        if (resnavigate) {
            dispatch(setPendingNavigation({ kind: 'tab', value: key }));
            dispatch(tryingnavigate(true));
            return;
        }
        setActiveTab(key);
    };

    useEffect(() => {
        if (!resnavigate && pendingNavigation?.kind === 'tab' && pendingNavigation.value) {
            setActiveTab(pendingNavigation.value);
            dispatch(clearPendingNavigation());
        }
    }, [resnavigate, pendingNavigation, dispatch]);

    // DoctorSettings reads selectedDoctor.doctorId + doctorSettings from store,
    // so hydrate both when the page mounts directly via the sidebar (no upstream
    // profile fetch happened in that flow).
    useEffect(() => {
        if (!doctorId || bootstrapped.current) return;
        if (!selectedDoctor || selectedDoctor.doctorId !== doctorId) {
            dispatch(getDoctorById(doctorId));
        }
        dispatch(getDoctorSettings(doctorId));
        bootstrapped.current = true;
    }, [doctorId, selectedDoctor, dispatch]);

    const tabs = [
        { key: 'calendar', label: t('doctorSettingsPage.tab_calendar', 'Calendar') },
        { key: 'prescription', label: t('doctorSettingsPage.tab_prescription', 'Prescription') },
    ];

    return (
        <div
            data-testid="doctor-settings-page"
            className="bg-white rounded-2xl p-4 shadow-xl border border-gray-100 sm:p-6 mx-auto"
        >
            <div className="mb-6" data-testid="doctor-settings-header">
                <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent" data-testid="doctor-settings-title">
                    {t('doctorprofile.tab8', 'Settings')}
                </h1>
            </div>

            <div
                data-testid="doctor-settings-tabs"
                className="hidden sm:flex w-fit flex-wrap justify-start gap-2 bg-gradient-to-r from-gray-50 to-blue-50/30 p-1 rounded-2xl shadow border border-gray-200 mb-6"
            >
                {tabs.map(({ key, label }) => (
                    <button
                        key={key}
                        type="button"
                        data-testid={`doctor-settings-tab-${key}`}
                        onClick={() => handleTabChange(key)}
                        className={`relative px-6 py-3 text-sm font-semibold transition-colors duration-300 rounded-xl z-10 ${
                            activeTab === key ? 'text-white shadow-md' : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                        }`}
                    >
                        {activeTab === key && (
                            <motion.div
                                layoutId="activeDoctorSettingsTab"
                                className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-lg !m-0"
                                transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                            />
                        )}
                        <span className="relative z-20">{label}</span>
                    </button>
                ))}
            </div>

            <div className="sm:hidden mb-4">
                <select
                    value={activeTab}
                    onChange={(e) => handleTabChange(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-gray-700 font-semibold focus:outline-none focus:border-[#4ABDBD]"
                    data-testid="doctor-settings-tabs-mobile"
                >
                    {tabs.map(({ key, label }) => (
                        <option key={key} value={key} data-testid={`doctor-settings-tab-option-${key}`}>{label}</option>
                    ))}
                </select>
            </div>

            <div data-testid="doctor-settings-content">
                {activeTab === 'calendar' && <DoctorSettings />}
                {activeTab === 'prescription' && <PrescriptionFieldsTab />}
            </div>
        </div>
    );
}

export default DoctorSettingsPage;
